import { CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { DatePicker } from '@/components/molecules/date-picker';
import { NumberInput, useNumberField } from '@/components/molecules/NumberInput';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import type {
  EstimateBookingType,
  EstimateRecurringEndType,
  EstimateRecurringFrequency,
  EstimateRecurringIntervalUnit,
} from '@/types/estimate';

// 0=Sun … 6=Sat, matching RecurringConfig.customDays. (NOT-240)
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

interface EstimateScheduleSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  readOnly?: boolean;
  bookingType: EstimateBookingType;
  onBookingTypeChange: (value: EstimateBookingType) => void;
  recurringStartDate: string;
  onRecurringStartDateChange: (value: string) => void;
  recurringDaysOfWeek: number[];
  onRecurringDaysOfWeekChange: (value: number[]) => void;
  recurringFrequency: EstimateRecurringFrequency;
  onRecurringFrequencyChange: (value: EstimateRecurringFrequency) => void;
  recurringEndType: EstimateRecurringEndType;
  onRecurringEndTypeChange: (value: EstimateRecurringEndType) => void;
  recurringInterval: number;
  onRecurringIntervalChange: (value: number) => void;
  recurringIntervalUnit: EstimateRecurringIntervalUnit;
  onRecurringIntervalUnitChange: (value: EstimateRecurringIntervalUnit) => void;
  recurringEndAfterCount: number;
  onRecurringEndAfterCountChange: (value: number) => void;
  recurringEndOnDate: string;
  onRecurringEndOnDateChange: (value: string) => void;
}

export function EstimateScheduleSection({
  open,
  onToggle,
  bare = false,
  readOnly = false,
  bookingType,
  onBookingTypeChange,
  recurringStartDate,
  onRecurringStartDateChange,
  recurringDaysOfWeek,
  onRecurringDaysOfWeekChange,
  recurringFrequency,
  onRecurringFrequencyChange,
  recurringEndType,
  onRecurringEndTypeChange,
  recurringInterval,
  onRecurringIntervalChange,
  recurringIntervalUnit,
  onRecurringIntervalUnitChange,
  recurringEndAfterCount,
  onRecurringEndAfterCountChange,
  recurringEndOnDate,
  onRecurringEndOnDateChange,
}: EstimateScheduleSectionProps) {
  const isRecurring = bookingType === 'RECURRING';
  // NOT-240: weekday picker applies to weekly/biweekly and custom-by-weeks.
  const showDayPicker =
    recurringFrequency === 'WEEKLY' ||
    recurringFrequency === 'BIWEEKLY' ||
    (recurringFrequency === 'CUSTOM' && recurringIntervalUnit === 'WEEKS');
  const toggleDay = (day: number) => {
    const set = new Set(recurringDaysOfWeek);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    onRecurringDaysOfWeekChange([...set].sort((a, b) => a - b));
  };

  // An emptied interval reads as 0 (an incomplete recurrence the form rejects),
  // but the box stays empty so a new figure can be typed straight over the old.
  const intervalField = useNumberField({
    value: recurringInterval || null,
    emptyValue: 0,
    allowDecimal: false,
    onChange: (next) => onRecurringIntervalChange(next ?? 0),
  });

  const body = (
    <>
      <div className="flex items-center gap-2">
        <Switch
          id="booking-type-recurring"
          checked={isRecurring}
          disabled={readOnly}
          onCheckedChange={(value) => onBookingTypeChange(value ? 'RECURRING' : 'ONE_OFF')}
        />
        <Label
          htmlFor="booking-type-recurring"
          className="cursor-pointer text-sm text-muted-foreground"
        >
          Recurring
        </Label>
      </div>
      <div className={!isRecurring ? 'pointer-events-none select-none opacity-40' : ''}>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Start date</Label>
            <DatePicker
              disabled={readOnly}
              value={recurringStartDate ? new Date(recurringStartDate + 'T00:00:00') : undefined}
              onChange={(date) =>
                onRecurringStartDateChange(date ? format(date, 'yyyy-MM-dd') : '')
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Frequency</Label>
            <Select
              disabled={readOnly}
              value={recurringFrequency}
              onValueChange={(value) =>
                onRecurringFrequencyChange(value as EstimateRecurringFrequency)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WEEKLY">Weekly</SelectItem>
                <SelectItem value="BIWEEKLY">Biweekly</SelectItem>
                <SelectItem value="MONTHLY">Monthly</SelectItem>
                <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                <SelectItem value="ANNUALLY">Annually</SelectItem>
                <SelectItem value="CUSTOM">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Ends</Label>
            <Select
              disabled={readOnly}
              value={recurringEndType}
              onValueChange={(value) => onRecurringEndTypeChange(value as EstimateRecurringEndType)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INDEFINITE">Never</SelectItem>
                <SelectItem value="AFTER_COUNT">After N visits</SelectItem>
                <SelectItem value="ON_DATE">On specific date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {showDayPicker && (
          <div className="mt-3 space-y-1.5">
            <Label className="text-sm font-medium">Repeat on</Label>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((day) => {
                const selected = recurringDaysOfWeek.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    disabled={readOnly}
                    onClick={() => toggleDay(day.value)}
                    className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Leave empty to repeat on the start date&rsquo;s weekday.
            </p>
          </div>
        )}
        {(recurringFrequency === 'CUSTOM' || recurringEndType !== 'INDEFINITE') && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div />
            {recurringFrequency === 'CUSTOM' ? (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Every</Label>
                <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring">
                  <input
                    id="recurring-interval"
                    min={1}
                    disabled={readOnly}
                    placeholder="1"
                    {...intervalField}
                    className="w-16 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  />
                  <div className="border-l border-input">
                    <Select
                      disabled={readOnly}
                      value={recurringIntervalUnit}
                      onValueChange={(value) =>
                        onRecurringIntervalUnitChange(value as EstimateRecurringIntervalUnit)
                      }
                    >
                      <SelectTrigger className="h-9 border-0 bg-transparent px-3 text-sm shadow-none focus:ring-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAYS">Days</SelectItem>
                        <SelectItem value="WEEKS">Weeks</SelectItem>
                        <SelectItem value="MONTHS">Months</SelectItem>
                        <SelectItem value="YEARS">Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ) : (
              <div />
            )}
            {recurringEndType === 'AFTER_COUNT' ? (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Visits</Label>
                <NumberInput
                  id="recurring-end-count"
                  min={1}
                  disabled={readOnly}
                  value={recurringEndAfterCount || null}
                  emptyValue={0}
                  allowDecimal={false}
                  onChange={(next) => onRecurringEndAfterCountChange(next ?? 0)}
                />
              </div>
            ) : recurringEndType === 'ON_DATE' ? (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">End date</Label>
                <DatePicker
                  disabled={readOnly}
                  value={
                    recurringEndOnDate ? new Date(recurringEndOnDate + 'T00:00:00') : undefined
                  }
                  onChange={(date) =>
                    onRecurringEndOnDateChange(date ? format(date, 'yyyy-MM-dd') : '')
                  }
                />
              </div>
            ) : (
              <div />
            )}
          </div>
        )}
      </div>
    </>
  );

  if (bare) {
    return <div className="space-y-4">{body}</div>;
  }

  return (
    <FormAccordionCard
      open={open}
      header={
        <FormSectionHeader
          icon={CalendarDays}
          title="Schedule"
          open={open}
          onToggle={onToggle}
          statusBadge={bookingType && bookingType !== 'ONE_OFF' ? 'Set' : undefined}
        />
      }
      bodyClassName="space-y-4"
    >
      {body}
    </FormAccordionCard>
  );
}
